import { isVideoFile } from "./chatVideo";
import { isLocalArticleImage } from "./compressArticleImage";

const hasLocalVideo = (files) =>
    (files || []).some(
        (file) =>
            file
            && (file.isVideo || file.fileType === "video" || isVideoFile(file))
            && (file.local || (typeof File !== "undefined" && file instanceof File) || String(file.url || "").startsWith("blob:"))
    );

export function createPublishProgressTracker({ files, onUpdate }) {
    const list = files || [];
    const hasVideo = hasLocalVideo(list);
    const hasImages = list.some(isLocalArticleImage);
    const stages = [];
    if (hasVideo) stages.push({ id: "compressing", weight: 38 });
    if (hasImages) stages.push({ id: "media", weight: 12 });
    stages.push({ id: "uploading", weight: hasVideo || hasImages ? 42 : 70 });
    stages.push({ id: "finishing", weight: 8 });
    const total = stages.reduce((sum, stage) => sum + stage.weight, 0);

    const setStage = (id, phasePct = 0) => {
        const pct = Math.max(0, Math.min(100, Number(phasePct) || 0));
        let acc = 0;
        for (const stage of stages) {
            if (stage.id === id) {
                acc += stage.weight * (pct / 100);
                break;
            }
            acc += stage.weight;
        }
        onUpdate({
            phase: id,
            percent: Math.max(2, Math.min(99, Math.round((acc / total) * 100))),
        });
    };

    return { setStage, hasVideo, hasImages };
}

export function publishProgressLabel(phase, strings = {}) {
    if (phase === "compressing") {
        return strings.article_publish_compressing_video || strings.video_processing || "Compressing video…";
    }
    if (phase === "media") {
        return strings.article_publish_preparing_media || "Preparing photos…";
    }
    if (phase === "uploading") {
        return strings.article_publish_uploading || strings.video_uploading || "Uploading…";
    }
    if (phase === "finishing") {
        return strings.article_publish_finishing || "Publishing…";
    }
    return strings.article_publish_preparing || "Preparing…";
}

export async function waitUntilArticleVideosReady({
    getChain,
    getFiles,
    getVideoMeta,
    onProgress,
}) {
    const poll = setInterval(() => {
        const files = getFiles?.() || [];
        const metas = getVideoMeta?.() || {};
        const active = files.filter((file) =>
            !!(file && (file.processing || (file.clientId && metas[file.clientId]?.processing)))
        );
        if (!active.length) return;
        const avg = active.reduce((sum, file) => {
            const meta = file.clientId ? metas[file.clientId] : null;
            return sum + (meta?.progress || file.progress || 0);
        }, 0) / active.length;
        onProgress?.(avg);
    }, 200);
    try {
        await (typeof getChain === "function" ? getChain() : Promise.resolve());
    } finally {
        clearInterval(poll);
        onProgress?.(100);
    }
}
